import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Cell, Label, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { EventActivityFeed } from '@/components/activity/EventActivityFeed';
import { TeamActiveEffectsCard } from '@/components/teams/TeamActiveEffectsCard';

import { useTeamDetailContext } from './teamDetailContext';

interface TeamStats {
  totalPoints: number;
  challengesCompleted: number;
  totalChallenges: number;
  rank: number;
  totalTeams: number;
  hintsUsed: number;
}

const DONUT_COLORS = {
  completed: 'hsl(var(--primary))',
  remaining: 'hsl(var(--muted))',
} as const;

// Shared card chrome — exact match for the "Event Info" panel on
// the event Overview tab (rounded-xl border, no internal padding so
// the bordered header sits flush, p-6 body).
const CARD_CLASSES = 'rounded-xl border bg-card shadow-sm py-0 gap-0';
const CARD_HEADER_CLASSES = 'px-6 py-4 !pb-4 border-b';
const CARD_CONTENT_CLASSES = 'p-6';

/**
 * Team overview. Mirrors the event Overview tab's card pattern —
 * each section is its own bordered card with a title + description
 * header — so a facilitator moving between event and team pages
 * sees the same visual chrome. Layout: Team Stats spans the full
 * width at the top, then Challenge Progress (donut) and Team
 * Activity (shared feed) sit side by side below.
 */
export function EventTeamOverviewTab() {
  const { team, eventId, teamId } = useTeamDetailContext();
  const apiClient = useApiClient();

  const { data: statsResp, isLoading: statsLoading } = useQuery<{
    success: boolean;
    data: TeamStats;
  }>({
    queryKey: ['EventTeamStats', eventId, teamId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams/${teamId}/stats`),
    enabled: !!eventId && !!teamId,
  });
  const stats = statsResp?.data;

  const completed = stats?.challengesCompleted ?? 0;
  const total = stats?.totalChallenges ?? 0;
  const remaining = Math.max(0, total - completed);
  const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Facilitator effect controls — only renders when the team has
          active artifact effects (retime / dismiss). */}
      <TeamActiveEffectsCard eventId={eventId} teamId={teamId} />

      {/* Team Stats — full-width card matching the event Overview's
          "Event Info" pattern: bordered title row, then a fields
          grid below. */}
      <Card className={CARD_CLASSES}>
        <CardHeader className={CARD_HEADER_CLASSES}>
          <CardTitle>Team Stats</CardTitle>
          <CardDescription>Performance and engagement at a glance</CardDescription>
        </CardHeader>
        <CardContent className={CARD_CONTENT_CLASSES}>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Total Points" value={stats?.totalPoints ?? 0} loading={statsLoading} />
            <Stat
              label="Rank"
              value={stats?.rank ? `#${stats.rank}` : '—'}
              loading={statsLoading}
              helperText={stats?.totalTeams ? `of ${stats.totalTeams}` : undefined}
            />
            <Stat label="Members" value={team.memberCount ?? 0} loading={false} />
            <Stat label="Hints Used" value={stats?.hintsUsed ?? 0} loading={statsLoading} />
          </div>
        </CardContent>
      </Card>

      {/* Two-column body: progress donut on the left, activity feed
          on the right. Both cards reuse the event-info chrome so
          the page reads as one consistent grid. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className={CARD_CLASSES}>
          <CardHeader className={CARD_HEADER_CLASSES}>
            <CardTitle>Challenge Progress</CardTitle>
            <CardDescription>Challenges completed out of all available</CardDescription>
          </CardHeader>
          <CardContent className={CARD_CONTENT_CLASSES}>
            {statsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Skeleton className="h-48 w-48 rounded-full" />
              </div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Completed', value: completed },
                        { name: 'Remaining', value: remaining },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none">
                      <Cell fill={DONUT_COLORS.completed} />
                      <Cell fill={DONUT_COLORS.remaining} />
                      <Label
                        position="center"
                        content={() => (
                          <text
                            x="50%"
                            y="50%"
                            textAnchor="middle"
                            dominantBaseline="central">
                            <tspan
                              x="50%"
                              dy="-0.4em"
                              className="fill-foreground text-3xl font-semibold">
                              {completionPct}%
                            </tspan>
                            <tspan
                              x="50%"
                              dy="1.6em"
                              className="fill-muted-foreground text-xs">
                              {completed} of {total}
                            </tspan>
                          </text>
                        )}
                      />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity feed inherits the same card chrome via its
            `card` variant — title + description + bordered header. */}
        <EventActivityFeed
          eventId={eventId}
          teamId={teamId}
          title="Team Activity"
          description="Recent submissions, hints, and points earned"
        />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  loading,
  helperText,
}: {
  label: string;
  value: string | number;
  loading: boolean;
  helperText?: string;
}) {
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-1">{label}</p>
      {loading ? (
        <Skeleton className="h-8 w-16" />
      ) : (
        <p className="text-2xl font-semibold">{value}</p>
      )}
      {helperText && !loading && (
        <p className="mt-0.5 text-xs text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}
